// Shared, null-safe formatters for the Customers pages.
//
// The bridge proxies an external product API whose fields can arrive null or
// absent, so every formatter degrades to an em dash rather than throwing.

import type { TrialStatus } from './customer-types'

/** Canonical "no value" glyph used across the Customers pages. */
export const EMPTY = '—'

const DATE_FMT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }

/** ISO date/datetime → "12 Jun 2026". Null/invalid → em dash. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return EMPTY
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? EMPTY : d.toLocaleDateString('en-GB', DATE_FMT)
}

/** ISO → "12 Jun 2026, 14:30". Null/invalid → em dash. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return EMPTY
  return d.toLocaleString('en-GB', { ...DATE_FMT, hour: '2-digit', minute: '2-digit' })
}

/** Friendly relative time ("Today", "3 days ago"). Falls back to a date. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return EMPTY
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return EMPTY
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  if (days < 0) return formatDate(iso)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 30) return `${days} days ago`
  return formatDate(iso)
}

/** GBP money. Whole pounds show no decimals; otherwise 2dp. Null → em dash. */
export function formatGbp(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return EMPTY
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** Title-case a plan id ('trial' → 'Trial', 'pro_annual' → 'Pro Annual'). */
export function planLabel(plan: string): string {
  return prettifyLabel(plan)
}

/** UI label + tone for a trial/plan state. */
export function trialStatusMeta(
  status: TrialStatus,
  daysRemaining: number | null,
): { label: string; tone: 'success' | 'warning' | 'secondary' } {
  switch (status) {
    case 'trialing':
      return {
        label: daysRemaining != null ? `Trial · ${daysRemaining}d left` : 'Trial',
        tone: daysRemaining != null && daysRemaining <= 7 ? 'warning' : 'secondary',
      }
    case 'expired':
      return { label: 'Trial expired', tone: 'warning' }
    case 'converted':
      return { label: 'Paying', tone: 'success' }
  }
}

// ─── Additional details (hybrid auto-show) ──────────────────────────────────
// Renders any product field not yet promoted to a curated, colour-coded field
// above. Labels prettify automatically; values format by inferred type. New
// fields added product-side appear here on their own, no code change required.

/**
 * Hand-curated label overrides. Auto-prettify handles the common case
 * (snake_case / camelCase → Title Case); add an entry here only to rename a
 * specific field by hand — e.g. acronyms or symbols the auto-rule gets wrong.
 */
export const ADDITIONAL_FIELD_LABELS: Record<string, string> = {
  // Example overrides (uncomment / extend as fields are added product-side):
  // co2_emissions: 'CO₂ Emissions',
  // grossVehicleWeight: 'Gross Vehicle Weight (kg)',
}

/** Tokens that should keep a fixed casing instead of Title Case. */
const ACRONYMS: Record<string, string> = {
  id: 'ID',
  url: 'URL',
  vin: 'VIN',
  mot: 'MOT',
  pmi: 'PMI',
  co2: 'CO₂',
  gbp: 'GBP',
  api: 'API',
  vrm: 'VRM',
  hgv: 'HGV',
  psv: 'PSV',
}

/** snake_case / camelCase / kebab-case → Title Case, honouring the override map. */
export function prettifyLabel(key: string): string {
  const override = ADDITIONAL_FIELD_LABELS[key]
  if (override) return override
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase → spaced
    .replace(/[_-]+/g, ' ') // snake/kebab → spaced
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ].*)?$/
const MONEY_KEY_RE = /(price|amount|cost|fee|mrr|revenue|total|gbp|balance|charge|invoice)/i

/** Format one additional-field value by inferred type. Null/empty → em dash. */
export function formatAdditionalValue(key: string, value: unknown): string {
  if (value == null || value === '') return EMPTY
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  if (typeof value === 'number') {
    if (Number.isNaN(value)) return EMPTY
    if (/pence$/i.test(key)) return formatGbp(value / 100) // integer-pence columns
    if (MONEY_KEY_RE.test(key)) return formatGbp(value)
    return new Intl.NumberFormat('en-GB').format(value)
  }

  if (typeof value === 'string') {
    if (ISO_DATE_RE.test(value)) {
      const d = new Date(value)
      if (!Number.isNaN(d.getTime())) {
        return value.includes('T') ? formatDateTime(value) : formatDate(value)
      }
    }
    return value
  }

  // Arrays / nested objects: compact JSON as a last resort.
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export interface AdditionalFieldPair {
  key: string
  label: string
  value: string
}

/** Normalise an `additionalFields` record into sorted label/value pairs. */
export function toAdditionalFieldPairs(
  fields: Record<string, unknown> | undefined | null,
): AdditionalFieldPair[] {
  if (!fields) return []
  return Object.entries(fields)
    .map(([key, raw]) => ({ key, label: prettifyLabel(key), value: formatAdditionalValue(key, raw) }))
    .sort((a, b) => a.label.localeCompare(b.label))
}
